import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { userAPI } from '../../services/api';
import styles from './PointsHistory.module.css';

const PointsHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, income, expense
  const [dateRange, setDateRange] = useState('month'); // week, month, quarter, year

  useEffect(() => {
    fetchPointsHistory();
  }, [filter, dateRange]);

  const fetchPointsHistory = async () => {
    setLoading(true);
    try {
      // 构建查询参数
      const params = {
        page: 1,
        limit: 100, // 获取更多数据用于前端筛选和统计
        type: filter
      };

      // 根据时间范围添加日期参数
      const now = new Date();
      let startDate = null;

      switch (dateRange) {
        case 'week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'quarter':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case 'year':
          startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          break;
        default:
          break;
      }

      if (startDate) {
        params.startDate = startDate.toISOString().split('T')[0]; // YYYY-MM-DD format
      }

      // 调用真实API
      const response = await userAPI.getPointsHistory(params);
      console.log('Points history response:', response);

      if (response.data) {
        const apiData = response.data.data;

        // 转换API数据格式以适应前端组件
        const transformedTransactions = (apiData.list || []).map(transaction => ({
          id: transaction.id,
          type: mapTransactionType(transaction.transactionType || transaction.type),
          amount: transaction.amount,
          description: transaction.description || transaction.remark || '金币变动',
          date: transaction.createdAt || transaction.createTime,
          referenceId: transaction.referenceId,
          // status: transaction.status === 'SUCCESS' ? 'completed' : 'pending'
        }));

        setTransactions(transformedTransactions);
      } else {
        console.error('API returned error:', response.data?.message || 'Unknown error');
        setTransactions([]);
      }

    } catch (error) {
      console.error('Failed to fetch points history:', error);
      // 如果API调用失败，设置空数组
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // 映射后端交易类型到前端显示类型
  const mapTransactionType = (backendType) => {
    const typeMapping = {
      'SIGN_IN': 'checkin',
      'INVITE': 'invite',
      'PURCHASE': 'purchase',
      'BONUS': 'bonus',
      'REFUND': 'refund',
      'REWARD': 'bonus',
      'DEDUCTION': 'purchase',
      'OTHER': 'other'
    };
    return typeMapping[backendType] || 'other';
  };

  const getTransactionIcon = (type) => {
    const icons = {
      checkin: '📅',
      invite: '👥',
      purchase: '🛒',
      bonus: '🎁',
      refund: '💰',
      other: '📋'
    };
    return icons[type] || icons.other;
  };

  const getTransactionColor = (amount) => {
    return amount > 0 ? '#52c41a' : '#ff4d4f';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return '今天';
    if (diffDays === 1) return '昨天';
    if (diffDays < 7) return `${diffDays}天前`;
    return date.toLocaleDateString('zh-CN');
  };

  const calculateSummary = () => {
    const income = transactions
      .filter(t => t.amount > 0)
      .reduce((sum, t) => sum + t.amount, 0);

    const expense = Math.abs(transactions
      .filter(t => t.amount < 0)
      .reduce((sum, t) => sum + t.amount, 0));

    return { income, expense, net: income - expense };
  };

  const summary = calculateSummary();

  if (loading) {
    return (
      <div className={styles.pointsHistory}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          <span>加载中...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pointsHistory}>
      <div className={styles.historyHeader}>
        <h2>金币流水</h2>
        <p>查看您的金币收支记录和变化详情</p>
      </div>

      <div className={styles.summaryCards}>
        <div className={`${styles.summaryCard} ${styles.income}`}>
          <div className={styles.summaryIcon}>📈</div>
          <div className={styles.summaryContent}>
            <span className={styles.summaryValue}>+{summary.income}</span>
            <span className={styles.summaryLabel}>本期收入</span>
          </div>
        </div>
        <div className={`${styles.summaryCard} ${styles.expense}`}>
          <div className={styles.summaryIcon}>📉</div>
          <div className={styles.summaryContent}>
            <span className={styles.summaryValue}>-{summary.expense}</span>
            <span className={styles.summaryLabel}>本期支出</span>
          </div>
        </div>
        <div className={`${styles.summaryCard} ${styles.net}`}>
          <div className={styles.summaryIcon}>💎</div>
          <div className={styles.summaryContent}>
            <span className={styles.summaryValue}>{summary.net > 0 ? '+' : ''}{summary.net}</span>
            <span className={styles.summaryLabel}>净收益</span>
          </div>
        </div>
      </div>

      <div className={styles.filterSection}>
        <div className={styles.filterGroup}>
          <label>类型筛选：</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">全部</option>
            <option value="income">收入</option>
            <option value="expense">支出</option>
          </select>
        </div>
        <div className={styles.filterGroup}>
          <label>时间范围：</label>
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="week">最近一周</option>
            <option value="month">最近一月</option>
            <option value="quarter">最近三月</option>
            <option value="year">最近一年</option>
          </select>
        </div>
      </div>

      <div className={styles.transactionsList}>
        {transactions.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📊</div>
            <h3>暂无记录</h3>
            <p>当前筛选条件下没有找到金币记录</p>
          </div>
        ) : (
          transactions.map(transaction => (
            <div key={transaction.id} className={styles.transactionItem}>
              <div className={styles.transactionIcon}>
                {getTransactionIcon(transaction.type)}
              </div>
              <div className={styles.transactionInfo}>
                <div className={styles.transactionDescription}>
                  {transaction.description}
                </div>
                <div className={styles.transactionMeta}>
                  <span className={styles.transactionDate}>
                    {formatDate(transaction.date)}
                  </span>
                  {transaction.type === 'purchase' && transaction.referenceId && (
                    <Link
                      to={`/resource/${transaction.referenceId}`}
                      className={styles.resourceLink}
                    >
                      查看资源详情
                    </Link>
                  )}
                </div>
              </div>
              <div
                className={styles.transactionAmount}
                style={{ color: getTransactionColor(transaction.amount) }}
              >
                {transaction.amount > 0 ? '+' : ''}{transaction.amount}
              </div>
            </div>
          ))
        )}
      </div>

      {transactions.length > 0 && (
        <div className={styles.paginationInfo}>
          <span>共 {transactions.length} 条记录</span>
        </div>
      )}
    </div>
  );
};

export default PointsHistory;